import { useContext, useEffect, useCallback } from "react";
import IUpdateSupplierDTO from "../../../models/DTO/IUpdateSupplierDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import ISupplierModel from "../../../models/ISupplierModel";
import OwTitle from "../../shared/OwTitle/OwTitle";
import SupplierProfileOptions from "./SupplierProfileOptions/SupplierProfileOptions";
import IOnUpdateSupplierDTO from "../../../models/DTO/IOnUpdateSupplierDTO";
import SupplierContext from "@contexts/SupplierContext";
import UserContext from "@contexts/UserContext";
import { SocketContext } from "@contexts/SocketContext";
import UiContext from "@contexts/UiContext";
import IUiSocketSetup from "@models/IUiSocketSetup";
import { ConfirmWorkflowChangeModal } from "./ConfirmWorkflowChangeModal/ConfirmWorkflowChangeModal";
import INotification from "@models/INotification";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import { useNavigate } from "react-router";

const BusinessSettings = () => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);
    const navigate = useNavigate();

    const onUpdateSupplier = (data: IOnUpdateSupplierDTO) => {
        const notification: INotification = {
            message: "successfully_updated_settings",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        uiContext.notifyFn(null, notification);

        // Close any open modals when update is complete
        closeModal();
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.SupplierOnUpdateSupplier, fn: onUpdateSupplier }];

    useEffect(() => {
        sockets.forEach((_socket) => {
            socket.on(_socket.endpoint, _socket.fn);
        });

        return () => {
            sockets.forEach((_socket) => {
                socket.off(_socket.endpoint, _socket.fn);
            });
        };
    }, []);

    const updateSupplier = (_supplierModel: ISupplierModel) => {
        const payload: IUpdateSupplierDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            supplierModel: _supplierModel,
        };

        socket.emit(EndpointsEnum.SupplierUpdateSupplier, payload);
    };

    const closeModal = () => {
        uiContext.setModalComponent(null);
        uiContext.showModal(false);
    };

    const createConfirmSubmit = (_supplierModel: ISupplierModel) => () => {
        updateSupplier(_supplierModel);
        closeModal();
    };

    const cancelSubmit = useCallback(() => {
        closeModal();
    }, []);

    const openModal = (_supplierModel: ISupplierModel) => {
        const modalComponent = (
            <ConfirmWorkflowChangeModal confirmSubmit={createConfirmSubmit(_supplierModel)} cancelSubmit={cancelSubmit} />
        );

        uiContext.setModalComponent(modalComponent);
        uiContext.showModal(true);
    };

    const performSave = (_supplierModel: ISupplierModel, hasOrderProgressStateChanges?: boolean) => {
        if (hasOrderProgressStateChanges) {
            openModal(_supplierModel);
        } else {
            // as a safety, we're not even going to send this across unless explicitly changed.
            delete supplierModel.order_progress_states;

            updateSupplier(_supplierModel);
        }
    };

    return (
        <div className="BusinessSettings">
            <div className={"row"}>
                <div className={"col-12 "}>
                    <SupplierProfileOptions
                        userModel={userModel}
                        supplierModel={supplierModel}
                        saveFn={performSave}
                        socket={socket}
                        notifyFn={uiContext.notifyFn}
                        toggleSidebar={uiContext.toggleSidebar}
                        setSidebarComponent={uiContext.setSidebarComponent}
                        setActivityIndicator={uiContext.setActivityIndicator}
                        showHiddenFeatures={uiContext.showHiddenFeatures}
                        navigate={navigate}
                    />
                </div>
            </div>
        </div>
    );
};

export default BusinessSettings;
